import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import './styles.css';
import { SimulationEngine } from './simulation/SimulationEngine';
import { CityState } from './simulation/types';
import { ThreeRenderer } from './render/ThreeRenderer';

import { HUD } from './ui/HUD';
import { BuildToolbar, ToolType } from './ui/BuildToolbar';
import { PanelController } from './ui/PanelController';
import { 
  ZoomIn, ZoomOut, Compass, AlertTriangle, MapPin, 
  Zap, Droplet, Award, CheckCircle, RotateCw, Trash2, TrendingUp 
} from 'lucide-react';

const App: React.FC = () => {
  const simEngineRef = useRef<SimulationEngine | null>(null);
  const threeRendererRef = useRef<ThreeRenderer | null>(null);

  const [cityState, setCityState] = useState<CityState | null>(null);
  const [activeTool, setActiveTool] = useState<ToolType>('select');
  const [activePanel, setActivePanel] = useState<'economy' | 'none'>('none');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // High-Fidelity UI states
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<string>('none');
  const [showQuests, setShowQuests] = useState<boolean>(true);

  // 1. Initialize Headless Simulation Engine and Three.js visualizer on mount
  useEffect(() => {
    // A. Create Simulation Engine
    const sim = new SimulationEngine();
    simEngineRef.current = sim;
    setCityState({ ...sim.getState() });

    // B. Create Three.js Visualizer Diorama
    console.log("[DEBUG] [main.tsx] Bootstrapping Three.js 3D Diorama viewport.");
    const renderer = new ThreeRenderer('phaser-game', sim, (x, y) => {
      if (x === -1) {
        setSelectedCell(null);
      } else {
        setSelectedCell({ x, y });
      }
    });
    threeRendererRef.current = renderer;
    
    // Start simulation clock
    sim.start();

    // C. Subscribe React to Sim Events
    const handleStateUpdate = (newState: CityState) => {
      setCityState({ ...newState });
    };

    sim.on('tick', handleStateUpdate);
    sim.on('state-changed', handleStateUpdate);

    // D. Cleanup on unmount
    return () => {
      sim.stop();
      sim.off('tick', handleStateUpdate);
      sim.off('state-changed', handleStateUpdate);
      
      if (threeRendererRef.current) {
        threeRendererRef.current.destroy();
      }
    };
  }, []);

  // --- ACTIONS BRIDGES ---

  const handleSetSpeed = (speed: number) => {
    simEngineRef.current?.setSpeed(speed);
    showToast(`Velocità impostata: ${speed}x`);
  };

  const handleSelectTool = (tool: ToolType) => {
    setActiveTool(tool);
    setSelectedCell(null); // Deselect on tool switch
    if (threeRendererRef.current) {
      threeRendererRef.current.activeTool = tool;
    }
  };

  const handleSetTaxRate = (rate: number) => {
    simEngineRef.current?.setTaxRate(rate);
  };

  const handleSave = () => {
    if (!simEngineRef.current) return;
    const serialized = simEngineRef.current.saveCity();
    localStorage.setItem('swissland_save_state', serialized);
    showToast('Città Salvata con Successo!');
  };

  const handleLoad = () => {
    if (!simEngineRef.current) return;
    const saved = localStorage.getItem('swissland_save_state');
    if (saved) {
      simEngineRef.current.loadCity(saved);
      showToast('Salvataggio Caricato con Successo!');
    } else {
      showToast('Nessun salvataggio trovato!');
    }
  };

  const handleReset = () => {
    if (!simEngineRef.current) return;
    
    simEngineRef.current.stop();

    // Create a brand new Simulation instance
    const freshSim = new SimulationEngine();
    simEngineRef.current = freshSim;
    setCityState({ ...freshSim.getState() });

    // Destroy and recreate Three.js visualizer
    if (threeRendererRef.current) {
      threeRendererRef.current.destroy();
    }

    const renderer = new ThreeRenderer('phaser-game', freshSim, (x, y) => {
      if (x === -1) {
        setSelectedCell(null);
      } else {
        setSelectedCell({ x, y });
      }
    });
    threeRendererRef.current = renderer;
    freshSim.start();

    handleSelectTool('select');
    setActivePanel('none');
    setSelectedCell(null);
    setActiveOverlay('none');
    showToast('Città Azzerata con Successo!');
  };

  const handleTriggerZoom = (level: 'macro' | 'meso' | 'micro') => {
    if (threeRendererRef.current) {
      threeRendererRef.current.triggerZoom(level);
      showToast(`Zoom impostato: ${level.toUpperCase()}`);
    }
  };

  const handleSetOverlay = (overlay: string) => {
    setActiveOverlay(overlay);
    if (threeRendererRef.current) {
      threeRendererRef.current.activeOverlay = overlay;
    }
    showToast(`Mappa termica attiva: ${overlay.toUpperCase()}`);
  };

  const handleRotateCamera = (direction: 'left' | 'right') => {
    if (threeRendererRef.current) {
      threeRendererRef.current.rotateCamera(direction);
      showToast(`Camera ruotata a 90°`);
    }
  };

  const handlePurchaseSector = (sx: number, sy: number) => {
    if (simEngineRef.current?.unlockSector(sx, sy)) {
      showToast(`Territorio (${sx}, ${sy}) sbloccato!`);
    } else {
      showToast(`Fondi insufficienti per espanderti!`);
    }
  };

  const handleManualUpgrade = (x: number, y: number) => {
    if (!simEngineRef.current || !cityState) return;
    const key = `${x},${y}`;
    const b = cityState.buildings[key];
    if (!b) return;

    // Enforce power & water
    const isPowered = !!cityState.powerGrid[key] || b.familyId.includes('power');
    const isWatered = !!cityState.waterGrid[key] || b.familyId.includes('water');

    if (!isPowered || !isWatered) {
      showToast(`Impossibile sbloccare upgrade: servono Elettricità e Acqua!`);
      return;
    }

    const upgradeCost = b.level * 150;
    if (cityState.treasury < upgradeCost) {
      showToast(`Fondi insufficienti! Upgrade costa ${upgradeCost} §`);
      return;
    }

    // Spend funds and redevelop to next level
    cityState.treasury -= upgradeCost;
    b.level += 1;
    b.stage = 'planned'; // put back under construction
    b.progress = 0;
    b.occupancy = 0;

    simEngineRef.current.addHistory(`Mayoral Upgrade: Livellato manualmente "${b.name}" al livello ${b.level}!`);
    showToast(`Avviato cantiere di upgrade per §${upgradeCost}!`);
    setSelectedCell(null);
  };

  const handleDisaster = (type: 'tornado' | 'fire_outbreak') => {
    if (simEngineRef.current) {
      simEngineRef.current.triggerDisaster(type);
      showToast(`Innescato disastro: ${type.toUpperCase()}`);
    }
  };

  const handleSocialEvent = (type: 'festival' | 'block_party') => {
    if (simEngineRef.current?.triggerEvent(type)) {
      showToast(`Finanziata ${type.toUpperCase()}!`);
    } else {
      showToast(`Fondi erariali insufficienti per finanziare l'evento!`);
    }
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(prev => prev === message ? null : prev);
    }, 2200);
  };

  // Resolve diagnostic properties of clicked cell
  const getCellDiagnostics = () => {
    if (!selectedCell || !cityState) return null;
    const { x, y } = selectedCell;
    const key = `${x},${y}`;

    const terrainNames = ['Acqua', 'Spiaggia', 'Pianura Erbosa', 'Montagna'];
    const terrain = terrainNames[cityState.terrain[y]?.[x]] || 'Sconosciuto';

    const b = cityState.buildings[key];
    const parcel = cityState.parcels[key];
    const isUnlocked = !!cityState.unlockedSectors[`${Math.floor(x / 16)},${Math.floor(y / 16)}`];

    const power = !!cityState.powerGrid[key] || (b?.zone === 'civic' && b?.familyId.includes('power'));
    const water = !!cityState.waterGrid[key] || (b?.zone === 'civic' && b?.familyId.includes('water'));
    const pollution = cityState.pollutionGrid[key] || 0;
    const traffic = cityState.trafficGrid[key] || 0;
    const landValue = cityState.landValueGrid[key] || 100;
    const crime = cityState.crimeGrid[key] || 5;
    const fireRisk = cityState.fireGrid[key] || 5;

    return {
      x,
      y,
      terrain,
      b,
      parcel,
      isUnlocked,
      power,
      water,
      pollution,
      traffic,
      landValue,
      crime,
      fireRisk
    };
  };

  const diag = getCellDiagnostics();

  return (
    <div className="game-container" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* 3D Diorama Canvas container */}
      <div id="phaser-game" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />

      {/* Absolute Loading Overlay */}
      {!cityState && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#263238', color: 'white', zIndex: 1000000 }}>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Caricamento SWISSLAND 3D...</h2>
          <p style={{ fontSize: '14px', color: '#90a4ae' }}>Inizializzazione del diorama 3D in corso</p>
        </div>
      )}

      {/* React HTML User Interface HUD Overlays */}
      {cityState && (
        <div className="ui-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10, pointerEvents: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '16px' }}>
          
          {/* TOP PANEL: HUD */}
          <HUD 
            state={cityState} 
            onSetSpeed={handleSetSpeed} 
            onTogglePanel={setActivePanel}
            activePanel={activePanel}
          />

          {/* LEFT INTERACTIVE TOOL CONTAINER (Camera Snaps, Heatmaps overlays, and rotation) */}
          <div className="panel-toggles-container" style={{ position: 'absolute', top: '90px', left: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            
            {/* Camera Actions */}
            <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', borderRadius: '10px', pointerEvents: 'auto' }}>
              <span style={{ fontSize: '10px', fontWeight: 800, color: '#90a4ae', textAlign: 'center' }}>CAMERA</span>
              <button className="btn-panel-toggle" onClick={() => handleTriggerZoom('macro')} title="Zoom Macro">
                <ZoomOut size={13} />
                <span>Macro</span>
              </button>
              <button className="btn-panel-toggle" onClick={() => handleTriggerZoom('meso')} title="Zoom Meso">
                <Compass size={13} />
                <span>Meso</span>
              </button>
              <button className="btn-panel-toggle" onClick={() => handleTriggerZoom('micro')} title="Zoom Micro">
                <ZoomIn size={13} />
                <span>Micro</span>
              </button>
              <button className="btn-panel-toggle" onClick={() => handleRotateCamera('right')} title="Ruota Camera">
                <RotateCw size={13} />
                <span>Ruota 90°</span>
              </button>
            </div>

            {/* Heatmaps Overlays (Phase 11) */}
            <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '8px', borderRadius: '10px', pointerEvents: 'auto' }}>
              <span style={{ fontSize: '10px', fontWeight: 800, color: '#90a4ae', textAlign: 'center' }}>FILTRI DIORAMA</span>
              <button className={`btn-panel-toggle ${activeOverlay === 'none' ? 'active' : ''}`} onClick={() => handleSetOverlay('none')}>
                <span>Libera</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'power' ? 'active' : ''}`} onClick={() => handleSetOverlay('power')} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Zap size={11} style={{ color: '#0288d1' }} />
                <span>Energia</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'water' ? 'active' : ''}`} onClick={() => handleSetOverlay('water')} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Droplet size={11} style={{ color: '#00bcd4' }} />
                <span>Acqua</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'traffic' ? 'active' : ''}`} onClick={() => handleSetOverlay('traffic')}>
                <span>Traffico</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'pollution' ? 'active' : ''}`} onClick={() => handleSetOverlay('pollution')}>
                <span>Inquinamento</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'crime' ? 'active' : ''}`} onClick={() => handleSetOverlay('crime')}>
                <span>Criminalità</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'fire' ? 'active' : ''}`} onClick={() => handleSetOverlay('fire')}>
                <span>Rischio Fuoco</span>
              </button>
              <button className={`btn-panel-toggle ${activeOverlay === 'landvalue' ? 'active' : ''}`} onClick={() => handleSetOverlay('landvalue')}>
                <span>Valore Terreno</span>
              </button>
            </div>
          </div>

          {/* MAYOR QUEST LOG CARD PANEL (Phase 8) */}
          {showQuests && simEngineRef.current && (
            <div className="glass-card" style={{ position: 'absolute', top: '90px', right: '16px', width: '280px', padding: '12px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'auto', maxHeight: '350px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#e0f2f1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Award size={14} style={{ color: '#ffeb3b' }} />
                  <span>OBIETTIVI MAYOR</span>
                </span>
                <button onClick={() => setShowQuests(false)} style={{ border: 'none', background: 'transparent', color: '#90a4ae', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>nascondi</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {simEngineRef.current.getQuests().map((q) => (
                  <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '8px', border: q.isCompleted ? '1px solid rgba(76, 175, 80, 0.25)' : '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: q.isCompleted ? '#a5d6a7' : '#ffffff', textDecoration: q.isCompleted ? 'line-through' : 'none' }}>{q.title}</span>
                      {q.isCompleted && <CheckCircle size={12} style={{ color: '#4caf50' }} />}
                    </div>
                    <p style={{ fontSize: '10px', color: '#b0bec5', margin: 0 }}>{q.description}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 800, marginTop: '4px', color: '#80deea' }}>
                      <span>Progresso:</span>
                      <span>{q.progressText}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showQuests && (
            <button onClick={() => setShowQuests(true)} className="btn-panel-toggle" style={{ position: 'absolute', top: '90px', right: '16px', pointerEvents: 'auto' }}>
              <Award size={13} style={{ color: '#ffeb3b' }} />
              <span>Quest Log</span>
            </button>
          )}

          {/* DIAGNOSTICS CARD PANEL (Bottom Left corner, displays clicked cell information) */}
          {diag && activeTool === 'select' && (
            <div className="glass-card" style={{ position: 'absolute', bottom: '110px', left: '16px', width: '280px', padding: '14px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'auto', border: '1px solid rgba(255,255,255,0.15)' }}>
              
              {/* Coordinates / Terrain Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#80deea', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <MapPin size={13} />
                  <span>CELLA ({diag.x}, {diag.y})</span>
                </span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#e0f2f1' }}>{diag.terrain}</span>
              </div>

              {/* Locked Sector Purchasing logic */}
              {!diag.isUnlocked ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(229, 57, 53, 0.1)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(229, 57, 53, 0.2)' }}>
                  <p style={{ fontSize: '11px', color: '#ef9a9a', margin: 0, fontWeight: 700 }}>⚠️ QUADRANTE TERRENO BLOCCATO!</p>
                  <p style={{ fontSize: '10px', color: '#cfd8dc', margin: 0 }}>Espandi Swisslandia sbloccando questo settore della griglia.</p>
                  <button 
                    onClick={() => handlePurchaseSector(Math.floor(diag.x / 16), Math.floor(diag.y / 16))}
                    className="btn-action-primary"
                    style={{ padding: '8px', fontSize: '11px', fontWeight: 800, width: '100%', cursor: 'pointer', background: '#ef5350', border: 'none', borderRadius: '6px', color: 'white' }}
                  >
                    Acquista Quadrante (§{(Object.keys(cityState.unlockedSectors).length * 2500).toLocaleString()})
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  
                  {/* Building details */}
                  {diag.b ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>{diag.b.name}</span>
                        <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px', fontWeight: 800, color: '#e0f2f1' }}>
                          LVL {diag.b.level}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#b0bec5', marginTop: '4px' }}>
                        <span>Stato:</span>
                        <span style={{ fontWeight: 800, color: diag.b.stage === 'completed' ? '#4caf50' : (diag.b.stage === 'damaged' ? '#ef5350' : '#ffa726') }}>
                          {diag.b.stage.toUpperCase()}
                        </span>
                      </div>

                      {/* Residents / occupancy stats */}
                      {(diag.b.zone === 'residential' || diag.b.zone === 'commercial' || diag.b.zone === 'industrial' || diag.b.zone === 'office') && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#b0bec5' }}>
                          <span>{diag.b.zone === 'residential' ? 'Abitanti:' : 'Posti Occupati:'}</span>
                          <span style={{ fontWeight: 800 }}>{diag.b.occupancy} / {diag.b.capacity}</span>
                        </div>
                      )}

                      {/* Manual Upgrade Button (Phase 12) */}
                      {diag.b.stage === 'completed' && diag.b.level < 6 && (
                        <button
                          onClick={() => handleManualUpgrade(diag.x, diag.y)}
                          style={{
                            marginTop: '8px',
                            padding: '6px',
                            fontSize: '10px',
                            fontWeight: 800,
                            borderRadius: '4px',
                            border: 'none',
                            background: '#009688',
                            color: 'white',
                            cursor: 'pointer',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <TrendingUp size={11} />
                          <span>Upgrade Edificio (§{diag.b.level * 150})</span>
                        </button>
                      )}
                    </div>
                  ) : diag.parcel ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(255,255,255,0.04)', padding: '6px 10px', borderRadius: '8px', fontSize: '11px', color: '#e0f2f1' }}>
                      <span>Zona verniciata:</span>
                      <span style={{ fontWeight: 800, textTransform: 'capitalize' }}>
                        {diag.parcel.zone} ({diag.parcel.density.toUpperCase()})
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#90a4ae', fontStyle: 'italic', textAlign: 'center', padding: '6px' }}>
                      Nessuna struttura o zona attiva
                    </div>
                  )}

                  {/* Utilities connection checks */}
                  <div style={{ display: 'flex', justifyContent: 'space-around', margin: '6px 0', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '6px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: diag.power ? 1.0 : 0.4 }} title={diag.power ? 'Energia Attiva' : 'Manca Energia'}>
                      <Zap size={14} style={{ color: '#ffeb3b' }} />
                      <span style={{ fontSize: '11px', fontWeight: 800, color: diag.power ? '#4caf50' : '#ff1744' }}>⚡ {diag.power ? 'OK' : 'OFF'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: diag.water ? 1.0 : 0.4 }} title={diag.water ? 'Utenza Idrica Attiva' : 'Manca Acqua'}>
                      <Droplet size={14} style={{ color: '#03a9f4' }} />
                      <span style={{ fontSize: '11px', fontWeight: 800, color: diag.water ? '#4caf50' : '#ff1744' }}>💧 {diag.water ? 'OK' : 'OFF'}</span>
                    </div>
                  </div>

                  {/* Soil environment diagnostics metrics */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '10px', color: '#b0bec5' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Valore del Terreno:</span>
                      <span style={{ fontWeight: 800, color: '#4caf50' }}>§ {diag.landValue} / mq</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Inquinamento locale:</span>
                      <span style={{ fontWeight: 800, color: diag.pollution > 0 ? '#ff1744' : '#b0bec5' }}>{diag.pollution} ppm</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Criminalità di Quartiere:</span>
                      <span style={{ fontWeight: 800, color: diag.crime > 50 ? '#ff1744' : '#b0bec5' }}>{diag.crime}%</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Rischio Incendio:</span>
                      <span style={{ fontWeight: 800, color: diag.fireRisk > 50 ? '#ff9800' : '#b0bec5' }}>{diag.fireRisk}%</span>
                    </div>
                  </div>
                  
                  {/* Fast Demolish directly in card */}
                  {(diag.b || diag.parcel) && (
                    <button
                      onClick={() => {
                        simEngineRef.current?.demolish(diag.x, diag.y);
                        showToast('Demolito cella con successo!');
                        setSelectedCell(null);
                      }}
                      style={{
                        marginTop: '10px',
                        padding: '6px',
                        fontSize: '10px',
                        fontWeight: 800,
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(255,23,68,0.15)',
                        color: '#ff1744',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <Trash2 size={11} />
                      <span>Rader Al Suolo (§10)</span>
                    </button>
                  )}

                </div>
              )}
            </div>
          )}

          {/* RIGHT SLIDE-OUT PANEL: Economy, Statistics and social events (Phase 9) */}
          {activePanel === 'economy' && (
            <PanelController 
              state={cityState}
              onSetTaxRate={handleSetTaxRate}
              onSave={handleSave}
              onLoad={handleLoad}
              onReset={handleReset}
              onClose={() => setActivePanel('none')}
              // Extend to allow disasters triggering and event funding!
              onTriggerDisaster={handleDisaster}
              onTriggerSocialEvent={handleSocialEvent}
            />
          )}

          {/* BOTTOM PANEL: Construction Toolbar */}
          <BuildToolbar 
            activeTool={activeTool} 
            onSelectTool={handleSelectTool} 
          />
        </div>
      )}

      {/* Floating alert notification toast */}
      {toastMessage && (
        <div style={{
          position: 'absolute',
          bottom: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(38, 50, 56, 0.95)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: 700,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)',
          zIndex: 100000,
          pointerEvents: 'none',
          animation: 'fadeInOut 2.2s ease'
        }}>
          {toastMessage}
        </div>
      )}

      {/* INTERACTIVE CRISIS POPUP MODAL */}
      {cityState && cityState.activeEvent && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100000,
          pointerEvents: 'auto'
        }}>
          <div className="glass-card" style={{
            width: '450px',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            border: '2px solid #26a69a',
            boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            color: '#37474f'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#c62828', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
              <AlertTriangle size={24} />
              <span>{cityState.activeEvent.title}</span>
            </h2>
            
            <p style={{ fontSize: '14px', color: '#37474f', lineHeight: '20px', fontWeight: 600, margin: 0 }}>
              {cityState.activeEvent.description}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
              {cityState.activeEvent.choices.map((choice) => (
                <button
                  key={choice.id}
                  onClick={() => {
                    const result = simEngineRef.current?.resolveActiveEventChoice(choice.id);
                    if (result) showToast(result);
                  }}
                  className="btn-action-primary"
                  style={{
                    padding: '12px',
                    fontSize: '13px',
                    fontWeight: 700,
                    textAlign: 'left',
                    background: '#37474f',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#26a69a';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#37474f';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span>{choice.label}</span>
                  {choice.cost !== 0 && (
                    <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                      {choice.cost > 0 ? `-${choice.cost} §` : `+${Math.abs(choice.cost)} §`}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(<App />);
}
